import React from 'react';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { RecordType } from '../../../__generated__/graphql';
import { ExpenseRecordType } from '../types';

type Props = {
  recordTypes: readonly RecordType[];
  onSelectRecordType: (recordType: RecordType) => void;
  loading?: boolean;
};

const i18Path = 'expenses.createEditModal';

export const ExpenseRecordTypeSelection: React.FC<Props> = ({
  recordTypes,
  onSelectRecordType,
  loading = false,
}) => {
  const t = useNamespacedTranslation(i18Path);

  const getRecordTypeIcon = (recordTypeDeveloperName: string) => {
    switch (recordTypeDeveloperName) {
      case ExpenseRecordType.PerDiem:
        return 'cafe-outline';
      case ExpenseRecordType.Reimbursement:
        return 'expenses-outline';
      case ExpenseRecordType.Mileage:
        return 'vehicle-icon';
      default:
        return 'document-outline';
    }
  };

  const getRecordTypeLabel = (recordType: RecordType) => {
    switch (recordType.DeveloperName) {
      case ExpenseRecordType.PerDiem:
        return t('recordTypes.perDiem');
      case ExpenseRecordType.Reimbursement:
        return t('recordTypes.reimbursement');
      case ExpenseRecordType.Mileage:
        return t('recordTypes.carMileage');
      default:
        return recordType.Label || recordType.Name;
    }
  };

  const getRecordTypeDescription = (recordTypeDeveloperName: string) => {
    switch (recordTypeDeveloperName) {
      case ExpenseRecordType.PerDiem:
        return t('recordTypes.perDiemDescription');
      case ExpenseRecordType.Reimbursement:
        return t('recordTypes.reimbursementDescription');
      case ExpenseRecordType.Mileage:
        return t('recordTypes.carMileageDescription');
      default:
        return '';
    }
  };

  return (
    <div className="expense-record-type-selection position-relative">
      {loading && (
        <div className="position-absolute top-50 start-50 translate-middle z-index-1000">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading')}...</span>
          </div>
        </div>
      )}

      <div className="text-center mb-4">
        <h3 className="mb-2">{t('chooseExpenseType')}</h3>
      </div>

      <div className={`row g-3 ${loading ? 'opacity-50' : ''}`}>
        {recordTypes.map((recordType) => (
          <div key={recordType.Id} className="col-md-4">
            <div
              className={`card h-100 border-2 expense-record-type-card border-primary ${
                loading ? 'expense-record-type-card--disabled' : ''
              }`}
              onClick={
                loading ? undefined : () => onSelectRecordType(recordType)
              }>
              <div className="card-body text-center p-4">
                <div className={`mb-3 text-primary`}>
                  <FlairIcon
                    icon={getRecordTypeIcon(recordType.DeveloperName)}
                    size="3x"
                  />
                </div>
                <h5 className={`card-title text-primary mb-2`}>
                  {getRecordTypeLabel(recordType)}
                </h5>
                <p className={`card-text text-primary-600 small`}>
                  {getRecordTypeDescription(recordType.DeveloperName)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
